import type { ResourceArticle } from "../types";

// Public editorial adaptation of docs/marketing/Competitor_Research_Pack/06_SIMINO_SOCIAL_VS_LOOMLY.md
export const article6: ResourceArticle = {
  "slug": "simino-social-vs-loomly",
  "title": "Simino Social vs Loomly",
  "description": "Compare Simino Social and Loomly: pricing research, workflow differences, strengths, and product direction to help you evaluate the right fit.",
  "category": "Comparison",
  "subject": "Loomly",
  "focus": "Calendars and team collaboration",
  "summary": "Loomly is a fit when the team wants a mature calendar, generous collaboration, inspiration tools, approvals, and hands-on control of content operations.",
  "researchDate": "2026-09-20",
  "sourceFile": "06_SIMINO_SOCIAL_VS_LOOMLY.md",
  "sections": [
    {
      "id": "best-fit",
      "heading": "Best fit",
      "blocks": [
        {
          "type": "paragraph",
          "text": "**Choose Loomly when:** the team wants a mature calendar, generous collaboration, inspiration tools, approvals, and hands-on control of content operations."
        },
        {
          "type": "paragraph",
          "text": "**Choose Simino Social when the product vision is realized:** the business wants the product to begin with its outcome and prepare more of the plan, content, and follow-through while preserving review and override controls."
        }
      ]
    },
    {
      "id": "loomly-s-pricing-and-product-snapshot",
      "heading": "Loomly’s pricing and product snapshot",
      "blocks": [
        {
          "type": "paragraph",
          "text": "Loomly’s official pricing page lists:"
        },
        {
          "type": "list",
          "ordered": false,
          "items": [
            "Starter: $65/month monthly or $49/month billed yearly; 12 social accounts, 3 users, and unlimited calendars",
            "Beyond: $332/month monthly or $249/month billed yearly; 60 social accounts, unlimited users, and unlimited calendars",
            "Enterprise: custom for 61+ accounts"
          ]
        },
        {
          "type": "paragraph",
          "text": "Starter includes AI Assistant chat, scheduling, approval workflows and roles, advanced analytics, and link shorteners. The comparison also lists post generation, AI captions, AI replies, AI analytics, recurring posts, bulk posting, engagement tools, and content inspiration. AI usage and social-listening allowances differ by tier."
        }
      ]
    },
    {
      "id": "where-loomly-is-stronger",
      "heading": "Where Loomly is stronger",
      "blocks": [
        {
          "type": "list",
          "ordered": false,
          "items": [
            "Mature content calendar and planning UX",
            "Strong approvals, roles, and team collaboration",
            "Three users included in Starter and unlimited users in Beyond",
            "Unlimited calendars",
            "Broad content inspiration and media integrations",
            "Established agency and client workflow patterns"
          ]
        }
      ]
    },
    {
      "id": "the-strategic-difference",
      "heading": "The strategic difference",
      "blocks": [
        {
          "type": "table",
          "headers": [
            "Decision area",
            "Loomly",
            "Simino Social target"
          ],
          "rows": [
            [
              "Product center",
              "Calendar, collaboration, creation, and publishing",
              "Business goal transformed into an approved, brand-aware campaign"
            ],
            [
              "AI experience",
              "Assistant chat and AI functions in the toolset",
              "Coordinated AI roles that reuse brand memory and carry work through the lifecycle"
            ],
            [
              "Navigation",
              "Professional content-management workspace",
              "Operational command center: what AI is doing, what needs approval, what is next"
            ],
            [
              "Analytics",
              "Dashboards, reports, and AI analytics",
              "Explanation connected directly to the next campaign recommendation"
            ],
            [
              "Customer wedge",
              "Teams and agencies that plan content",
              "SMBs and creators that lack time or specialist staff to create the plan"
            ]
          ]
        }
      ]
    },
    {
      "id": "how-simino-social-can-create-more-value-for-money",
      "heading": "How Simino Social can create more value for money",
      "blocks": [
        {
          "type": "paragraph",
          "text": "Loomly’s included-user economics are strong, so Simino Social should not assume collaboration is an easy pricing advantage. It should compete on work eliminated:"
        },
        {
          "type": "list",
          "ordered": false,
          "items": [
            "Ask for the promotion goal instead of making the user build the calendar item by item",
            "Reuse brand facts, offers, tone, approved claims, and restrictions automatically",
            "Prepare platform-specific variants rather than duplicate one post blindly",
            "Let the user approve a campaign or policy, not every mechanical adaptation",
            "Explain performance in operational language and update the next recommendation",
            "Include lightweight agency/client authorization with clear permissions"
          ]
        }
      ]
    },
    {
      "id": "product-lesson-from-loomly",
      "heading": "Product lesson from Loomly",
      "blocks": [
        {
          "type": "paragraph",
          "text": "AI-native UX must still provide an excellent calendar, predictable approvals, media handling, and manual editing. Customers need to inspect, change, and override the AI’s work. “Agentic” is not an excuse to hide operational state."
        }
      ]
    },
    {
      "id": "sources",
      "heading": "Sources",
      "blocks": [
        {
          "type": "list",
          "ordered": false,
          "items": [
            "[Loomly pricing and plan comparison](https://www.loomly.com/pricing)"
          ]
        }
      ]
    }
  ]
};
